import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useSiteAdmin } from '@/hooks/useSiteAdmin';
import { ChevronLeft, Loader2, Globe, CheckCircle2, XCircle, ExternalLink, Rocket } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface WizardPublishProps {
  onPrevious: () => void;
  onComplete: () => void;
  siteId?: string;
}

interface PublishChecklist {
  aboutUsComplete: boolean;
  coverageSelected: boolean;
  homepageReady: boolean;
  domainConfigured: boolean;
}

export function WizardPublish({ onPrevious, onComplete, siteId }: WizardPublishProps) {
  const { toast } = useToast();
  const { setCanPublish, site, refetchSite } = useSiteAdmin();
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [checklist, setChecklist] = useState<PublishChecklist>({
    aboutUsComplete: false,
    coverageSelected: false,
    homepageReady: false,
    domainConfigured: false,
  });

  useEffect(() => {
    if (siteId) {
      checkPublishReadiness();
    }
  }, [siteId]);

  const checkPublishReadiness = async () => {
    if (!siteId) return;

    try {
      // Check site data
      const { data: siteData } = await supabase
        .from('sites')
        .select('company_name, contact_name, primary_domain')
        .eq('id', siteId)
        .single();

      // Check if towns are selected
      const { count: townCount } = await supabase
        .from('towns')
        .select('id', { count: 'exact', head: true })
        .eq('site_id', siteId)
        .eq('is_published', true);

      // Check homepage content
      const { data: homepageData } = await supabase
        .from('homepage_content')
        .select('draft_content')
        .eq('site_id', siteId)
        .maybeSingle();

      setChecklist({
        aboutUsComplete: !!(siteData?.company_name && siteData?.contact_name),
        coverageSelected: (townCount || 0) > 0,
        homepageReady: !!homepageData?.draft_content,
        domainConfigured: !!siteData?.primary_domain,
      });
    } catch (error) {
      console.error('Error checking publish readiness:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!siteId) return;

    setPublishing(true);
    try {
      // Publish homepage content
      const { data: homepage } = await supabase
        .from('homepage_content')
        .select('draft_content')
        .eq('site_id', siteId)
        .single();

      if (homepage) {
        await supabase
          .from('homepage_content')
          .update({
            published_content: homepage.draft_content,
            content_state: 'published',
            published_at: new Date().toISOString(),
          })
          .eq('site_id', siteId);
      }

      // Publish ads
      await supabase
        .from('site_advertisements')
        .update({
          ad_state: 'published',
          published_at: new Date().toISOString(),
        })
        .eq('site_id', siteId)
        .eq('ad_state', 'draft');

      // Update site status
      await supabase
        .from('sites')
        .update({ status: 'active' })
        .eq('id', siteId);

      // Enable publishing capability
      await setCanPublish(true);

      toast({
        title: '🎉 Site Published!',
        description: 'Your real estate website is now live.',
      });

      onComplete();
    } catch (error: any) {
      toast({
        title: 'Error publishing',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const allRequiredComplete = checklist.aboutUsComplete && checklist.coverageSelected;
  const previewUrl = site?.primary_domain 
    ? `https://${site.primary_domain}` 
    : `${window.location.origin}`;

  return (
    <div className="space-y-6">
      {/* Publish Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Publish Checklist
          </CardTitle>
          <CardDescription>
            Complete these items before publishing your site.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChecklistItem
            label="About Us information complete"
            complete={checklist.aboutUsComplete}
            required
          />
          <ChecklistItem
            label="Towns/neighborhoods selected"
            complete={checklist.coverageSelected}
            required
          />
          <ChecklistItem
            label="Homepage content ready"
            complete={checklist.homepageReady}
          />
          <ChecklistItem
            label="Custom domain configured"
            complete={checklist.domainConfigured}
          />
        </CardContent>
      </Card>

      {/* Domain Status */}
      <Card>
        <CardHeader>
          <CardTitle>Domain Status</CardTitle>
        </CardHeader>
        <CardContent>
          {site?.primary_domain ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-green-600" />
                  <span className="font-medium">{site.primary_domain}</span>
                </div>
                <Badge variant="default">Active</Badge>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Preview Site
                </a>
              </Button>
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                No custom domain configured. Your site will be accessible via a default URL after publishing.
                You can configure a custom domain later in Site Settings.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Publish Action */}
      <Card className="border-primary">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            {allRequiredComplete ? (
              <>
                <Rocket className="h-12 w-12 mx-auto text-primary" />
                <h3 className="text-lg font-semibold">Ready to Publish!</h3>
                <p className="text-muted-foreground">
                  Your site is ready to go live. Click the button below to publish.
                </p>
                <Button size="lg" onClick={handlePublish} disabled={publishing}>
                  {publishing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Rocket className="h-4 w-4 mr-2" />
                      Publish Site
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <XCircle className="h-12 w-12 mx-auto text-muted-foreground" />
                <h3 className="text-lg font-semibold">Not Ready Yet</h3>
                <p className="text-muted-foreground">
                  Please complete the required checklist items above before publishing.
                </p>
                <Button variant="outline" onClick={onPrevious}>
                  Go Back to Complete Steps
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <Button variant="ghost" onClick={onComplete}>
          Skip for now
        </Button>
      </div>
    </div>
  );
}

function ChecklistItem({ 
  label, 
  complete, 
  required 
}: { 
  label: string; 
  complete: boolean; 
  required?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {complete ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <XCircle className="h-5 w-5 text-muted-foreground" />
        )}
        <span className={complete ? '' : 'text-muted-foreground'}>{label}</span>
      </div>
      {required && !complete && (
        <Badge variant="destructive" className="text-xs">Required</Badge>
      )}
    </div>
  );
}
